import { CalendarDate, CalendarDateTime } from '@internationalized/date';
import {
    createProperties,
    createRoot,
    type Field,
    Form,
    type FormState,
    type TypeToFields,
} from '@jmvdevelop/svelte-form';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import { notNullableOrThrow } from '$lib/utils/assert';
import { mergeObject } from '$lib/utils/mergeObject';
import { fail, ok, type Result } from '$lib/utils/result.js';
import { temporizePromise } from '$lib/utils/temporizePromise.js';
import { ImmURL } from '$lib/utils/url/ImmURL';
import { getUtmVariablesFromUrl } from '$lib/utils/utmVariables';
import type { JsonValue } from '$lib/utils/zod';
import { validateEmail } from '$lib/validator/email';
import { notBlankAndNotNull, notNull } from '$lib/validator/validator.js';
import {
    MultiQuote_CreateMutationDocument,
    type MultiQuote_CreateMutationMutation,
} from '$gql/graphql.js';
import { getRequestInfoInput } from '$components/Ad/utils';
import AgenceVoyageForm from '$components/MultiQuoteForm/AgenceVoyage/AgenceVoyageForm.svelte';
import * as AgenceVoyage from '$components/MultiQuoteForm/AgenceVoyage/AgenceVoyageFormData';
import ArchitecteForm from '$components/MultiQuoteForm/Architecte/ArchitecteForm.svelte';
import * as Architecte from '$components/MultiQuoteForm/Architecte/ArchitecteFormData';
import AssuranceForm from '$components/MultiQuoteForm/Assurance/AssuranceForm.svelte';
import * as Assurance from '$components/MultiQuoteForm/Assurance/AssuranceFormData';
import CarRentalForm from '$components/MultiQuoteForm/CarRental/CarRentalForm.svelte';
import * as CarRental from '$components/MultiQuoteForm/CarRental/CarRentalFormData';
import ConstructionForm from '$components/MultiQuoteForm/Construction/ConstructionForm.svelte';
import * as Construction from '$components/MultiQuoteForm/Construction/ConstructionFormData';
import ConstructionMetalliqueForm from '$components/MultiQuoteForm/ConstructionMetallique/ConstructionMetalliqueForm.svelte';
import * as ConstructionMetallique from '$components/MultiQuoteForm/ConstructionMetallique/ConstructionMetalliqueFormData';
import ConsultantForm from '$components/MultiQuoteForm/Consultant/ConsultantForm.svelte';
import * as Consultant from '$components/MultiQuoteForm/Consultant/ConsultantFormData';
import EnseignementSuperieurForm from '$components/MultiQuoteForm/EnseignementSuperieur/EnseignementSuperieurForm.svelte';
import * as EnseignementSuperieur from '$components/MultiQuoteForm/EnseignementSuperieur/EnseignementSuperieurFormData';
import EquipementMedicalForm from '$components/MultiQuoteForm/EquipementMedical/EquipementMedicalForm.svelte';
import * as EquipementMedical from '$components/MultiQuoteForm/EquipementMedical/EquipementMedicalFormData';
import ExpertComptableForm from '$components/MultiQuoteForm/ExpertComptable/ExpertComptableForm.svelte';
import * as ExpertComptable from '$components/MultiQuoteForm/ExpertComptable/ExpertComptableFormData';
import FroidClimatisationForm from '$components/MultiQuoteForm/FroidClimatisation/FroidClimatisationForm.svelte';
import * as FroidClimatisation from '$components/MultiQuoteForm/FroidClimatisation/FroidClimatisationFormData';
import FormationProForm from '$components/MultiQuoteForm/FormationPro/FormationProForm.svelte';
import * as FormationPro from '$components/MultiQuoteForm/FormationPro/FormationProFormData';
import GarageMecaniqueForm from '$components/MultiQuoteForm/GarageMecanique/GarageMecaniqueForm.svelte';
import * as GarageMecanique from '$components/MultiQuoteForm/GarageMecanique/GarageMecaniqueFormData';
import GardiennageSecuriteForm from '$components/MultiQuoteForm/GardiennageSecurite/GardiennageSecuriteForm.svelte';
import * as GardiennageSecurite from '$components/MultiQuoteForm/GardiennageSecurite/GardiennageSecuriteFormData';
import ImmobilierForm from '$components/MultiQuoteForm/Immobilier/ImmobilierForm.svelte';
import * as Immobilier from '$components/MultiQuoteForm/Immobilier/ImmobilierFormData';
import LaboratoireBtpForm from '$components/MultiQuoteForm/LaboratoireBtp/LaboratoireBtpForm.svelte';
import * as LaboratoireBtp from '$components/MultiQuoteForm/LaboratoireBtp/LaboratoireBtpFormData';
import NettoyageIndustrielForm from '$components/MultiQuoteForm/NettoyageIndustriel/NettoyageIndustrielForm.svelte';
import * as NettoyageIndustriel from '$components/MultiQuoteForm/NettoyageIndustriel/NettoyageIndustrielFormData';
import OfficeFurnitureForm from '$components/MultiQuoteForm/OfficeFurniture/OfficeFurnitureForm.svelte';
import * as OfficeFurniture from '$components/MultiQuoteForm/OfficeFurniture/OfficeFurnitureFormData';
import SolarForm from '$components/MultiQuoteForm/Solar/SolarForm.svelte';
import * as Solar from '$components/MultiQuoteForm/Solar/SolarFormData';
import TransitForm from '$components/MultiQuoteForm/Transit/TransitForm.svelte';
import * as Transit from '$components/MultiQuoteForm/Transit/TransitFormData';
import VisaEtudeForm from '$components/MultiQuoteForm/VisaEtude/VisaEtudeForm.svelte';
import * as VisaEtude from '$components/MultiQuoteForm/VisaEtude/VisaEtudeFormData';

export type MultiQuoteFormData = {
    user: number | null;
    message: string;
    categoryId: number | null;
    relatedCmsPostId: number | null;
    country: string | null;
    city: number | null;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    phone: string | null;
    agenceVoyage: AgenceVoyage.AgenceVoyageFormData;
    carRental: CarRental.CarRentalFormData;
    assurance: Assurance.AssuranceFormData;
    transit: Transit.TransitFormData;
    solar: Solar.SolarFormData;
    consultant: Consultant.ConsultantFormData;
    officeFurniture: OfficeFurniture.OfficeFurnitureFormData;
    construction: Construction.ConstructionFormData;
    constructionMetallique: ConstructionMetallique.ConstructionMetalliqueFormData;
    froidClimatisation: FroidClimatisation.FroidClimatisationFormData;
    visaEtude: VisaEtude.VisaEtudeFormData;
    gardiennageSecurite: GardiennageSecurite.GardiennageSecuriteFormData;
    architecte: Architecte.ArchitecteFormData;
    nettoyageIndustriel: NettoyageIndustriel.NettoyageIndustrielFormData;
    expertComptable: ExpertComptable.ExpertComptableFormData;
    equipementMedical: EquipementMedical.EquipementMedicalFormData;
    formationPro: FormationPro.FormationProFormData;
    enseignementSuperieur: EnseignementSuperieur.EnseignementSuperieurFormData;
    garageMecanique: GarageMecanique.GarageMecaniqueFormData;
    laboratoireBtp: LaboratoireBtp.LaboratoireBtpFormData;
    immobilier: Immobilier.ImmobilierFormData;
};

type FormData = MultiQuoteFormData;

export type Fields<TFormValues> = TypeToFields<
    TFormValues,
    FormData,
    keyof FormData,
    {
        agenceVoyage: AgenceVoyage.Fields<TFormValues>;
        carRental: CarRental.Fields<TFormValues>;
        assurance: Assurance.Fields<TFormValues>;
        transit: Transit.Fields<TFormValues>;
        solar: Solar.Fields<TFormValues>;
        consultant: Consultant.Fields<TFormValues>;
        officeFurniture: OfficeFurniture.Fields<TFormValues>;
        construction: Construction.Fields<TFormValues>;
        constructionMetallique: ConstructionMetallique.Fields<TFormValues>;
        froidClimatisation: FroidClimatisation.Fields<TFormValues>;
        visaEtude: VisaEtude.Fields<TFormValues>;
        gardiennageSecurite: GardiennageSecurite.Fields<TFormValues>;
        architecte: Architecte.Fields<TFormValues>;
        nettoyageIndustriel: NettoyageIndustriel.Fields<TFormValues>;
        expertComptable: ExpertComptable.Fields<TFormValues>;
        equipementMedical: EquipementMedical.Fields<TFormValues>;
        formationPro: FormationPro.Fields<TFormValues>;
        enseignementSuperieur: EnseignementSuperieur.Fields<TFormValues>;
        garageMecanique: GarageMecanique.Fields<TFormValues>;
        laboratoireBtp: LaboratoireBtp.Fields<TFormValues>;
        immobilier: Immobilier.Fields<TFormValues>;
    }
>;

export const empty: FormData = {
    user: null,
    message: '',
    categoryId: null,
    country: null,
    city: null,
    phone: null,
    firstname: null,
    lastname: null,
    email: null,
    relatedCmsPostId: null,
    agenceVoyage: AgenceVoyage.empty,
    carRental: CarRental.empty,
    assurance: Assurance.empty,
    transit: Transit.empty,
    solar: Solar.empty,
    consultant: Consultant.empty,
    officeFurniture: OfficeFurniture.empty,
    construction: Construction.empty,
    constructionMetallique: ConstructionMetallique.empty,
    froidClimatisation: FroidClimatisation.empty,
    visaEtude: VisaEtude.empty,
    gardiennageSecurite: GardiennageSecurite.empty,
    architecte: Architecte.empty,
    nettoyageIndustriel: NettoyageIndustriel.empty,
    expertComptable: ExpertComptable.empty,
    equipementMedical: EquipementMedical.empty,
    formationPro: FormationPro.empty,
    enseignementSuperieur: EnseignementSuperieur.empty,
    garageMecanique: GarageMecanique.empty,
    laboratoireBtp: LaboratoireBtp.empty,
    immobilier: Immobilier.empty,
};

export function createFields<TValues>(self: Field<TValues, FormData>): Fields<TValues> {
    const properties = createProperties(self, [
        'user',
        'message',
        'categoryId',
        'country',
        'city',
        'phone',
        'firstname',
        'lastname',
        'email',
        'relatedCmsPostId',
        'agenceVoyage',
        'carRental',
        'assurance',
        'transit',
        'solar',
        'consultant',
        'officeFurniture',
        'construction',
        'constructionMetallique',
        'froidClimatisation',
        'visaEtude',
        'gardiennageSecurite',
        'architecte',
        'nettoyageIndustriel',
        'expertComptable',
        'equipementMedical',
        'formationPro',
        'enseignementSuperieur',
        'garageMecanique',
        'laboratoireBtp',
        'immobilier',
    ] as const);

    return {
        self: self,
        ...properties,
        agenceVoyage: AgenceVoyage.createFields(properties.agenceVoyage),
        carRental: CarRental.createFields(properties.carRental),
        assurance: Assurance.createFields(properties.assurance),
        transit: Transit.createFields(properties.transit),
        solar: Solar.createFields(properties.solar),
        consultant: Consultant.createFields(properties.consultant),
        officeFurniture: OfficeFurniture.createFields(properties.officeFurniture),
        construction: Construction.createFields(properties.construction),
        constructionMetallique: ConstructionMetallique.createFields(
            properties.constructionMetallique,
        ),
        froidClimatisation: FroidClimatisation.createFields(
            properties.froidClimatisation,
        ),
        visaEtude: VisaEtude.createFields(properties.visaEtude),
        gardiennageSecurite: GardiennageSecurite.createFields(
            properties.gardiennageSecurite,
        ),
        architecte: Architecte.createFields(properties.architecte),
        nettoyageIndustriel: NettoyageIndustriel.createFields(
            properties.nettoyageIndustriel,
        ),
        expertComptable: ExpertComptable.createFields(properties.expertComptable),
        equipementMedical: EquipementMedical.createFields(properties.equipementMedical),
        formationPro: FormationPro.createFields(properties.formationPro),
        enseignementSuperieur: EnseignementSuperieur.createFields(
            properties.enseignementSuperieur,
        ),
        garageMecanique: GarageMecanique.createFields(properties.garageMecanique),
        laboratoireBtp: LaboratoireBtp.createFields(properties.laboratoireBtp),
        immobilier: Immobilier.createFields(properties.immobilier),
    };
}

export function validate(
    form: Form<FormData, string>,
    fields: Fields<FormData>,
    state: FormState<FormData, string>,
) {
    const values = state.values;

    if (values.user == null) {
        state = notBlankAndNotNull(values.firstname, {
            form,
            field: fields.firstname,
            message: 'Vous devez saisir votre prénom',
        })(state);

        state = notBlankAndNotNull(values.lastname, {
            form,
            field: fields.lastname,
            message: 'Vous devez saisir votre nom',
        })(state);

        state = validateEmail(values.email, {
            form,
            field: fields.email,
            notBlank: true,
            message: 'Vous devez saisir une adresse email valide',
        })(state);
    }

    state = notBlankAndNotNull(values.message, {
        form,
        field: fields.message,
        message: 'Vous devez saisir du texte comme message',
    })(state);

    state = notBlankAndNotNull(values.country, {
        form,
        field: fields.country,
        message: 'Vous devez choisir un pays',
    })(state);

    state = notNull(values.categoryId, {
        form,
        field: fields.categoryId,
        message: 'Vous devez specifier une catégorie',
    })(state);

    state = AgenceVoyage.validateAgenceVoyage(
        form,
        values.agenceVoyage,
        fields.agenceVoyage,
    )(state);
    state = CarRental.validateCarRental(form, values.carRental, fields.carRental)(state);
    state = Assurance.validateAssurance(form, values.assurance, fields.assurance)(state);
    state = Transit.validateTransit(form, values.transit, fields.transit)(state);
    state = Solar.validateSolar(form, values.solar, fields.solar)(state);

    state = Consultant.validateConsultant(
        form,
        values.consultant,
        fields.consultant,
    )(state);
    state = OfficeFurniture.validateOfficeFurniture(
        form,
        values.officeFurniture,
        fields.officeFurniture,
    )(state);
    state = Construction.validateConstruction(
        form,
        values.construction,
        fields.construction,
    )(state);
    state = VisaEtude.validateVisaEtude(form, values.visaEtude, fields.visaEtude)(state);
    state = GardiennageSecurite.validateGardiennageSecurite(
        form,
        values.gardiennageSecurite,
        fields.gardiennageSecurite,
    )(state);
    state = Architecte.validateArchitecte(
        form,
        values.architecte,
        fields.architecte,
    )(state);
    state = NettoyageIndustriel.validateNettoyageIndustriel(
        form,
        values.nettoyageIndustriel,
        fields.nettoyageIndustriel,
    )(state);
    state = ExpertComptable.validateExpertComptable(
        form,
        values.expertComptable,
        fields.expertComptable,
    )(state);
    state = EquipementMedical.validateEquipementMedical(
        form,
        values.equipementMedical,
        fields.equipementMedical,
    )(state);

    state = FormationPro.validateFormationPro(
        form,
        values.formationPro,
        fields.formationPro,
    )(state);

    state = EnseignementSuperieur.validateEnseignementSuperieur(
        form,
        values.enseignementSuperieur,
        fields.enseignementSuperieur,
    )(state);

    state = GarageMecanique.validateGarageMecanique(
        form,
        values.garageMecanique,
        fields.garageMecanique,
    )(state);

    state = LaboratoireBtp.validateLaboratoireBtp(
        form,
        values.laboratoireBtp,
        fields.laboratoireBtp,
    )(state);

    state = Immobilier.validateImmobilier(
        form,
        values.immobilier,
        fields.immobilier,
    )(state);

    state = ConstructionMetallique.validateConstructionMetallique(
        form,
        values.constructionMetallique,
        fields.constructionMetallique,
    )(state);

    state = FroidClimatisation.validateFroidClimatisation(
        form,
        values.froidClimatisation,
        fields.froidClimatisation,
    )(state);

    return state;
}

export function createForm(
    id: string,
    initialValues: Partial<FormData> = empty,
): [Form<FormData, string>, Fields<FormData>] {
    const fields = createFields<FormData>(createRoot<FormData>());
    const form = new Form<FormData, string>(
        { initialValues: mergeObject(empty, initialValues) },
        id,
    );

    form.addValidator((state) => {
        return validate(form, fields, state);
    });

    return [form, fields];
}

type OnSubmit = (
    form: Form<FormData, string>,
) => Promise<Result<MultiQuote_CreateMutationMutation['multiquote']['create'], string>>;

type ExtraValueScalar =
    | string
    | number
    | null
    | undefined
    | CalendarDateTime
    | CalendarDate;
type ExtraValue = ExtraValueScalar | readonly ExtraValueScalar[];

function isExtraValueArray(arg: ExtraValue): arg is readonly ExtraValueScalar[] {
    return Array.isArray(arg);
}

function formatExtraValue(
    deps: Pick<ShareableContainer, 'intl'>,
    value: ExtraValue,
): string {
    const { intl } = deps;

    if (value instanceof CalendarDateTime) {
        return intl().formatCalendarDateTime(value, 'PPPp');
    } else if (value instanceof CalendarDate) {
        return intl().formatCalendarDate(value, 'PPP');
    } else if (isExtraValueArray(value)) {
        return value.map((v) => formatExtraValue(deps, v)).join(', ');
    } else if (typeof value === 'number') {
        return intl().formatInt(value);
    } else if (typeof value === 'string') {
        return value;
    } else {
        return '';
    }
}

function formatExtraFields(
    deps: Pick<ShareableContainer, 'intl'>,
    fields: readonly Readonly<[string, ExtraValue]>[],
): { label: string; value: string }[] {
    const lines: { label: string; value: string }[] = [];
    for (const [label, value] of fields) {
        if (value != null) {
            lines.push({ label: label, value: formatExtraValue(deps, value) });
        }
    }

    return lines;
}

type CategoryStrategy = {
    useForCategoryId(categoryId: number): boolean;
    formatFields(
        deps: Pick<ShareableContainer, 'intl'>,
        values: FormData,
    ): JsonValue | null;
    extraComponentForm: typeof CarRentalForm;
};

const categoryStrategies: readonly CategoryStrategy[] = [
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 55;
        },
        formatFields(deps, values) {
            const froidClimatisation = values.froidClimatisation;
            return formatExtraFields(deps, [
                ['Quel est votre besoin principal ?', froidClimatisation.mainNeed],
                ['Quel type de bâtiment est concerné ?', froidClimatisation.buildingType],
                [
                    'Quelle est la surface approximative du lieu concerné en m2 ?',
                    froidClimatisation.surfaceArea,
                ],
                ["Quel est la ville de l'intervention ?", froidClimatisation.city],
                [
                    'Quand souhaitez-vous réaliser ce projet ?',
                    froidClimatisation.projectTimeline,
                ],
            ]);
        },
        extraComponentForm: FroidClimatisationForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 65;
        },
        formatFields(deps, values) {
            const constructionMetallique = values.constructionMetallique;
            return formatExtraFields(deps, [
                [
                    'Quel est le type de projet concerné ?',
                    constructionMetallique.projectType,
                ],
                [
                    'Quel usage est prévu pour la structure métallique ?',
                    constructionMetallique.structureUsage,
                ],
                [
                    'Quelle est la surface estimée de la structure ?',
                    constructionMetallique.surfaceArea,
                ],
                [
                    "Souhaitez-vous uniquement la fabrication, ou aussi l'installation ?",
                    constructionMetallique.serviceType,
                ],
                [
                    "Avez-vous besoin d'une prestation complémentaire ?",
                    constructionMetallique.additionalServices,
                ],
            ]);
        },
        extraComponentForm: ConstructionMetalliqueForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 284;
        },
        formatFields(deps, values) {
            const agenceVoyage = values.agenceVoyage;
            return formatExtraFields(deps, [
                [
                    'Quel type de voyage souhaitez-vous planifier ?',
                    agenceVoyage.travelType,
                ],
                ['Quel service vous intéresse le plus ?', agenceVoyage.serviceType],
                [
                    "Quelle est votre destination ou votre pays d'arrivée ?",
                    agenceVoyage.destination,
                ],
                ['Dates souhaitées aller', agenceVoyage.travelDateStart],
                ['Dates souhaitées retour', agenceVoyage.travelDateEnd],
            ]);
        },
        extraComponentForm: AgenceVoyageForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return (
                categoryId === 499 ||
                categoryId === 339 ||
                categoryId === 196 ||
                categoryId === 195
            );
        },
        formatFields(deps, values) {
            const immobilier = values.immobilier;
            return formatExtraFields(deps, [
                ['Quel type de bien recherchez-vous ?', immobilier.propertyType],
                ['Quel est votre objectif ?', immobilier.objective],
            ]);
        },
        extraComponentForm: ImmobilierForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 41;
        },
        formatFields(deps, values) {
            const garageMecanique = values.garageMecanique;
            return formatExtraFields(deps, [
                ['Quel type de service recherchez-vous ?', garageMecanique.serviceTypes],
                ['Pour quel type de véhicule ?', garageMecanique.vehicleType],
                [
                    'Quelle est la marque et le modèle de votre véhicule ?',
                    garageMecanique.vehicleBrandModel,
                ],
                ['Année du véhicule', garageMecanique.vehicleYear],
            ]);
        },
        extraComponentForm: GarageMecaniqueForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 411;
        },
        formatFields(deps, values) {
            const carRental = values.carRental;
            return formatExtraFields(deps, [
                ['Type de véhicule', carRental.vehicleType],
                ['Capacité du véhicule', carRental.vehicleCapacity],
                ['Chauffeur ?', carRental.withDriver],
                ['Lieu de prise en charge', carRental.pickupLocation],
                ['Date et heure de départ', carRental.departureDate],
                ['Date et heure de retour', carRental.returnDate],
            ]);
        },
        extraComponentForm: CarRentalForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 173 || categoryId === 174;
        },
        formatFields(deps, values) {
            const assurance = values.assurance;
            return formatExtraFields(deps, [
                ['Type d’assurance recherchée', assurance.assuranceType],
                [
                    'Êtes-vous un particulier ou un professionnel',
                    assurance.isIndividualOrProfessional,
                ],
                ...(assurance.assuranceType === 'other'
                    ? ([
                          [`Précisez le type d'assurance`, assurance.assuranceTypeOther],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'auto'
                    ? ([
                          ['Type de véhicule', assurance.vehicleType],
                          ['Marque et modèle', assurance.vehicleBrandAndModel],
                          ['Année de mise en circulation', assurance.vehicleYear],
                          ['Usage du véhicule', assurance.vehicleUsage],
                          ['Garantie souhaitée', assurance.desiredCoverage],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'home'
                    ? ([
                          ['Type de logement', assurance.housingType],
                          ['Propriétaire ou Locataire ?', assurance.isOwnerOrTenant],
                          ['Surface du logement', assurance.housingSurface],
                          ['Valeur du bien à assurer', assurance.insuredGoodsValue],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'health'
                    ? ([
                          ['Âge du souscripteur', assurance.subscriberAge],
                          [
                              'Nombre de personnes à couvrir',
                              assurance.numberOfPeopleCovered,
                          ],
                          [
                              'Niveau de remboursement souhaité',
                              assurance.desiredReimbursementLevel,
                          ],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'pro'
                    ? ([
                          ['Secteur d’activité', assurance.activitySector],
                          ['Nombre d’employés', assurance.employeeCount],
                          ['Type de couverture souhaitée', assurance.coverageType],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'travel'
                    ? ([
                          ['Destination du voyage', assurance.travelDestination],
                          ['Durée du séjour', assurance.travelDuration],
                          [
                              'Nombre de personnes à couvrir',
                              assurance.travelNumberOfPeople,
                          ],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'pet'
                    ? ([
                          ['Type d’animal', assurance.animalType],
                          ['Race de l’animal', assurance.animalBreed],
                          ['Âge de l’animal', assurance.animalAge],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'life'
                    ? ([
                          [
                              'Quel est votre objectif principal ?',
                              assurance.lifeMainObjective,
                          ],
                          ['Âge du souscripteur', assurance.lifeSubscriberAge],
                          ['Montant estimé à assurer', assurance.lifeEstimatedAmount],
                          [
                              'Bénéficiaire(s) désigné(s) ?',
                              assurance.lifeDesignatedBeneficiary,
                          ],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'borrower'
                    ? ([
                          ['Montant du prêt', assurance.borrowerLoanAmount],
                          ['Durée du prêt', assurance.borrowerLoanDuration],
                          [
                              'Type de bien financé',
                              assurance.borrowerPropertyTypeFinanced,
                          ],
                          [
                              'Avez-vous des problèmes de santé connus ?',
                              assurance.borrowerHasKnownHealthIssues,
                          ],
                          [
                              'Souhaitez-vous déléguer votre assurance (hors banque) ?',
                              assurance.borrowerWantsToDelegateInsurance,
                          ],
                      ] as const)
                    : []),
                ...(assurance.assuranceType === 'temporary'
                    ? ([
                          [
                              'Type de bien ou situation à assurer temporairement',
                              assurance.temporaryInsuredItemOrSituation,
                          ],
                          [
                              'Durée souhaitée de l’assurance',
                              assurance.temporaryDesiredDuration,
                          ],
                          [
                              'Motif de la souscription temporaire',
                              assurance.temporarySubscriptionReason,
                          ],
                          [
                              'Date de début de la couverture',
                              assurance.temporaryCoverageStartDate,
                          ],
                      ] as const)
                    : []),
            ]);
        },
        extraComponentForm: AssuranceForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 310 || categoryId === 323 || categoryId === 325;
        },
        formatFields(deps, values) {
            const transit = values.transit;
            return formatExtraFields(deps, [
                [`Quel type de transit recherchez-vous ?`, transit.transitType],
                [
                    `Votre marchandise est-elle déjà disponible ?`,
                    transit.isGoodsAvailable,
                ],
                [
                    `Quel est le point de départ de votre marchandise ?`,
                    transit.goodsStartPoint,
                ],
                [`Quelle est la destination finale ?`, transit.goodsFinalDestination],
                [
                    `Avez-vous besoin d’un service de dédouanement ?`,
                    transit.clearanceServiceRequired,
                ],
                [
                    `Souhaitez-vous un service d’entreposage temporaire ?`,
                    transit.temporaryStorageRequired,
                ],
                [`Quel est le type de marchandise ?`, transit.goodsType],
                [`Quel est le volume estimé de la marchandise ?`, transit.goodsVolume],
                [`Quel est le poids estimé de la marchandise ?`, transit.goodsWeight],
                [`Avez-vous des exigences particulières ?`, transit.specificRequirements],
                [`Quand souhaitez-vous organiser le transit ?`, transit.transitDate],
            ]);
        },
        extraComponentForm: TransitForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 167;
        },
        formatFields(deps, values) {
            const solar = values.solar;
            return formatExtraFields(deps, [
                [`Type de demande`, solar.requestType],
                [
                    `Consommation moyenne ou besoins estimés`,
                    solar.averageConsumptionOrEstimatedNeeds,
                ],
                [`Quel est votre objectif principal ?`, solar.mainObjective],
                [`Quand souhaitez-vous démarrer le projet ?`, solar.projectStartDate],
            ]);
        },
        extraComponentForm: SolarForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 143 || categoryId === 139;
        },
        formatFields(deps, values) {
            const consultant = values.consultant;
            return formatExtraFields(deps, [
                [`Type de besoin`, consultant.needType],
                [`Vous êtes un / une`, consultant.orgType],
            ]);
        },
        extraComponentForm: ConsultantForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 108;
        },
        formatFields(deps, values) {
            const officeFurniture = values.officeFurniture;
            return formatExtraFields(deps, [
                [`Vous êtes`, officeFurniture.entityType],
                [
                    `Quels types de mobilier recherchez-vous ?`,
                    officeFurniture.furnitureTypes,
                ],
                [`Volume estimé ou nombre de postes à équiper`, officeFurniture.volume],
            ]);
        },
        extraComponentForm: OfficeFurnitureForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 59 || categoryId === 67 || categoryId === 430;
        },
        formatFields(deps, values) {
            const construction = values.construction;
            return formatExtraFields(deps, [
                [
                    `Quel type de projet souhaitez-vous réaliser ?`,
                    construction.projectType,
                ],
                [`Pour quel type de bâtiment ?`, construction.buildingType],
            ]);
        },
        extraComponentForm: ConstructionForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 700;
        },
        formatFields(deps, values) {
            const visaEtude = values.visaEtude;
            return formatExtraFields(deps, [
                [`Dans quel pays souhaitez-vous étudier ?`, visaEtude.country],
                [
                    `Quel est votre niveau d’études actuel ?`,
                    visaEtude.currentEducationLevel,
                ],
                [`Quels services vous intéressent ?`, visaEtude.services],
            ]);
        },
        extraComponentForm: VisaEtudeForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 253 || categoryId === 254 || categoryId === 645;
        },
        formatFields(deps, values) {
            const gardiennageSecurite = values.gardiennageSecurite;
            return formatExtraFields(deps, [
                [
                    `Quels sont les lieux ou biens à sécuriser ?`,
                    gardiennageSecurite.placesToSecure,
                ],
                [`Quel type de besoin avez-vous ?`, gardiennageSecurite.needs],
                [
                    `Souhaitez-vous une assistance 24h/24 ?`,
                    gardiennageSecurite.assistance24h,
                ],
            ]);
        },
        extraComponentForm: GardiennageSecuriteForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 54;
        },
        formatFields(deps, values) {
            const architecte = values.architecte;
            return formatExtraFields(deps, [
                [`Quel type de projet souhaitez-vous réaliser ?`, architecte.projectType],
                [`Pour quel type de bâtiment ou d’espace ?`, architecte.buildingType],
            ]);
        },
        extraComponentForm: ArchitecteForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 216;
        },
        formatFields(deps, values) {
            const nettoyageIndustriel = values.nettoyageIndustriel;
            return formatExtraFields(deps, [
                [`Quel est votre besoin principal ?`, nettoyageIndustriel.needType],
                [
                    `À quelle fréquence souhaitez-vous ce service ?`,
                    nettoyageIndustriel.frequency,
                ],
                [
                    `Surface totale à nettoyer (en m² approximatif)`,
                    nettoyageIndustriel.surfaceArea,
                ],
            ]);
        },
        extraComponentForm: NettoyageIndustrielForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 140 || categoryId === 144;
        },
        formatFields(deps, values) {
            const expertComptable = values.expertComptable;
            return formatExtraFields(deps, [
                [
                    'Quel est le statut de votre entreprise ?',
                    expertComptable.businessStatus,
                ],
                ['Quels services recherchez-vous ?', expertComptable.services],
                [
                    'Souhaitez-vous un accompagnement ponctuel ou un suivi régulier ?',
                    expertComptable.supportType,
                ],
            ]);
        },
        extraComponentForm: ExpertComptableForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 239;
        },
        formatFields(deps, values) {
            const equipementMedical = values.equipementMedical;
            return formatExtraFields(deps, [
                ['Vous êtes ?', equipementMedical.youAre],
                ['Matériel médical recherché', equipementMedical.equipmentType],
                [
                    'Souhaitez-vous également un service associé ?',
                    equipementMedical.services,
                ],
            ]);
        },
        extraComponentForm: EquipementMedicalForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 191;
        },
        formatFields(deps, values) {
            const formationPro = values.formationPro;
            return formatExtraFields(deps, [
                [
                    'Dans quel domaine souhaitez-vous vous former ?',
                    formationPro.trainingDomain,
                ],
                [
                    'Quelle est votre principale motivation pour suivre une formation ?',
                    formationPro.mainMotivation,
                ],
                [
                    'Situation professionnelle actuelle, vous êtes :',
                    formationPro.professionalSituation,
                ],
            ]);
        },
        extraComponentForm: FormationProForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 188;
        },
        formatFields(deps, values) {
            const enseignementSuperieur = values.enseignementSuperieur;
            return formatExtraFields(deps, [
                ["Niveau d'études actuel", enseignementSuperieur.currentEducationLevel],
                [
                    'Dans quel domaine souhaitez-vous poursuivre vos études ?',
                    enseignementSuperieur.studyDomains,
                ],
                [
                    'Quel type de formation recherchez-vous ?',
                    enseignementSuperieur.formationType,
                ],
                [
                    'Quand souhaitez-vous commencer votre formation ?',
                    enseignementSuperieur.startDate,
                ],
            ]);
        },
        extraComponentForm: EnseignementSuperieurForm,
    },
    {
        useForCategoryId(categoryId: number) {
            return categoryId === 460;
        },
        formatFields(deps, values) {
            const laboratoireBtp = values.laboratoireBtp;
            return formatExtraFields(deps, [
                [
                    'Quel type de projet souhaitez-vous étudier ?',
                    laboratoireBtp.projectType,
                ],
                ['À quelle étape se trouve votre projet ?', laboratoireBtp.projectStage],
                [
                    'Quels types de prestations recherchez-vous ?',
                    laboratoireBtp.serviceTypes,
                ],
                [
                    'Avez-vous déjà réalisé une étude de sol ou des tests ?',
                    laboratoireBtp.soilStudyDone,
                ],
            ]);
        },
        extraComponentForm: LaboratoireBtpForm,
    },
];

export function getCategoryStrategyForCategoryId(
    categoryId: number | null,
): CategoryStrategy | null {
    if (categoryId == null) {
        return null;
    }
    return categoryStrategies.find((s) => s.useForCategoryId(categoryId)) ?? null;
}

function extractExtraFields(
    deps: Pick<ShareableContainer, 'intl'>,
    values: FormData,
): JsonValue | null {
    const strategy = getCategoryStrategyForCategoryId(values.categoryId);
    if (strategy != null) {
        return strategy.formatFields(deps, values);
    }
    return null;
}

export const createOnSubmit: (
    deps: Pick<ShareableContainer, 'intl' | 'apollo'>,
) => OnSubmit = (deps) => (form) => {
    return temporizePromise(
        1000,
        (async () => {
            try {
                const { apollo } = deps;
                const values = form.getState().values;
                const result = await apollo().mutate({
                    mutation: MultiQuote_CreateMutationDocument,
                    variables: {
                        input: {
                            message: values.message,
                            cityId: values.city,
                            phone: values.phone,
                            firstname: values.firstname,
                            lastname: values.lastname,
                            email: values.email,
                            relatedCmsPostId: values.relatedCmsPostId,
                            categoryId: notNullableOrThrow(values.categoryId),
                            country: notNullableOrThrow(values.country),
                            requestInfo: getRequestInfoInput(
                                new URL(window.document.location.href),
                            ),
                            utmVariables: getUtmVariablesFromUrl(
                                new ImmURL(window.document.location.href),
                            ),
                            extraFields: extractExtraFields(deps, values),
                        },
                    },
                });
                if (result.errors != null || result.data == null) {
                    return fail(result.errors?.toString() ?? 'Unknown error');
                }

                return ok(result.data.multiquote.create);
            } catch (e) {
                console.error(e);
                return fail('Erreur inconnue.');
            }
        })(),
    );
};
